import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import axiosInstance from '../api/axios';
import useAuthStore from '../store/authStore';
import { toast } from 'sonner';

import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectUrl = searchParams.get('redirect') || '/';
  const reason = searchParams.get('reason');
  const isAdminLogin = redirectUrl.includes('/admin');
  const setAuth = useAuthStore(state => state.setAuth);
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);

  // Show timeout message if redirected due to inactivity
  React.useEffect(() => {
    if (reason === 'timeout') {
      toast.warning('Sie wurden aufgrund von Inaktivität abgemeldet. Bitte melden Sie sich erneut an.');
    }
  }, [reason]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    console.log('[LOGIN] Starting login process');

    try {
      console.log('[LOGIN] Calling API with email:', formData.email);
      const response = await axiosInstance.post('/auth/login', formData);
      console.log('[LOGIN] API response received:', { hasUser: !!response.data.user, hasToken: !!response.data.token });
      
      const { user, token, claimed_orders_count } = response.data;
      
      // Check if user is admin and trying to access admin area
      const isAdminUser = user?.role === 'admin';
      let targetUrl = redirectUrl;
      
      // If trying to access admin but not admin user, go to homepage
      if (redirectUrl.includes('/admin') && !isAdminUser) {
        targetUrl = '/';
      }
      
      // If no specific redirect, admin users go to admin, others to homepage
      if (redirectUrl === '/' && isAdminUser) {
        targetUrl = '/admin';
      }
      
      console.log('[LOGIN] User role:', user?.role, 'isAdmin:', isAdminUser, 'Target URL:', targetUrl);
      
      // Save directly to localStorage FIRST before anything else
      const now = Date.now();
      const authState = { 
        version: 1,
        state: { 
          user, 
          token, 
          isAuthenticated: true, 
          lastActivity: now 
        }
      };
      
      try {
        localStorage.setItem('auth-storage', JSON.stringify(authState));
        localStorage.setItem('last-activity', now.toString());
        localStorage.setItem('token', token);
        console.log('[LOGIN] Direct localStorage save completed');
      } catch (storageError) {
        console.error('[LOGIN] localStorage save failed:', storageError);
      }
      
      // Then set auth state in Zustand
      console.log('[LOGIN] Calling setAuth');
      setAuth(user, token);
      
      // Verify localStorage was saved correctly
      const savedAuth = localStorage.getItem('auth-storage');
      console.log('[LOGIN] Verified localStorage saved:', !!savedAuth);
      
      toast.success('Erfolgreich angemeldet!');

      if (claimed_orders_count && claimed_orders_count > 0) {
        toast.success(
          claimed_orders_count === 1
            ? '1 frühere Gast-Bestellung wurde Ihrem Konto zugeordnet.'
            : `${claimed_orders_count} frühere Gast-Bestellungen wurden Ihrem Konto zugeordnet.`,
          { duration: 6000 }
        );
      }
      
      // Small delay to ensure storage is persisted on mobile browsers
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Navigate using window.location.href for full page reload
      console.log('[LOGIN] Navigating to:', targetUrl);
      window.location.href = targetUrl;
      
    } catch (error) {
      console.error('[LOGIN] Error:', error);
      toast.error(error.response?.data?.detail || 'Anmeldung fehlgeschlagen');
      setLoading(false);
    }
  };

  // Render with or without Navbar/Footer based on admin login
  const loginForm = (
    <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#F5F0EB' }}>
      <div className="max-w-4xl mx-auto">
        <div className="grid md:grid-cols-2 gap-8 items-start">
          
          {/* Benefits Section */}
          <div className="order-2 md:order-1">
            <div className="bg-gradient-to-br from-[#8B7355] to-[#6D5A47] rounded-2xl p-6 text-white">
              <h2 className="text-2xl font-bold mb-6">Deine Vorteile als Mitglied</h2>
              
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="bg-white/20 rounded-full p-2 flex-shrink-0">
                    <span className="text-2xl">🎁</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">10% Willkommens-Rabatt</h3>
                    <p className="text-white/80 text-sm">Sofort nach der Registrierung erhältst du deinen persönlichen Rabattcode</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="bg-white/20 rounded-full p-2 flex-shrink-0">
                    <span className="text-2xl">💝</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">Exklusive Coupons</h3>
                    <p className="text-white/80 text-sm">Regelmäßig neue Rabattcodes nur für registrierte Mitglieder</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="bg-white/20 rounded-full p-2 flex-shrink-0">
                    <span className="text-2xl">❤️</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">Persönliche Wunschliste</h3>
                    <p className="text-white/80 text-sm">Speichere deine Lieblingsprodukte und behalte sie im Blick</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="bg-white/20 rounded-full p-2 flex-shrink-0">
                    <span className="text-2xl">📦</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">Bestellübersicht</h3>
                    <p className="text-white/80 text-sm">Alle deine Bestellungen und Rechnungen an einem Ort</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="bg-white/20 rounded-full p-2 flex-shrink-0">
                    <span className="text-2xl">⚡</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">Schneller Checkout</h3>
                    <p className="text-white/80 text-sm">Deine Daten werden gespeichert für schnellere Bestellungen</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Login Form */}
          <div className="order-1 md:order-2">
            <Card className="shadow-xl">
              <CardHeader>
                <CardTitle className="text-2xl" data-testid="login-title">Anmelden</CardTitle>
                <CardDescription>Melden Sie sich bei Ihrem Konto an</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="email">E-Mail</Label>
                    <Input
                      id="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      data-testid="login-email-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="password">Passwort</Label>
                    <Input
                      id="password"
                      type="password"
                      required
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      data-testid="login-password-input"
                    />
                  </div>
                  <Button type="submit" className="w-full bg-[#8B7355] hover:bg-[#6D5A47]" disabled={loading} data-testid="login-submit-button">
                    {loading ? 'Wird geladen...' : 'Anmelden'}
                  </Button>
                </form>
                
                <div className="mt-4 text-center text-sm">
                  <Link to="/forgot-password" className="text-[#8B7355] hover:underline">
                    Passwort vergessen?
                  </Link>
                </div>
                
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <p className="text-center text-sm text-gray-600 mb-4">Noch kein Konto?</p>
                  <Link to="/register">
                    <Button variant="outline" className="w-full border-[#8B7355] text-[#8B7355] hover:bg-[#F5F0EB]">
                      Kostenlos registrieren
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
          
        </div>
      </div>
    </div>
  );

  // For admin login, render without Navbar/Footer
  if (isAdminLogin) {
    return loginForm;
  }

  // For customer login, render with Navbar/Footer
  return (
    <div className="App flex flex-col min-h-screen">
      <Navbar />
      <main className="flex-1">{loginForm}</main>
      <Footer />
    </div>
  );
};

export default Login;